import React, { forwardRef, useImperativeHandle } from 'react';
import { Icon } from "@/components/atoms";
import ShimmerTableRow from "@/components/atoms/ShimmerTableRow";
import EmptyState from "@/components/molecules/EmptyState/EmptyState";
import { useCustomDataTable } from '@/hooks/useCustomDataTable-hook';
import DataTableBody from "./DataTableBody";
import DataTableFilterRow from "./DataTableFilterRow";
import DataTableHead from "./DataTableHead";
import DataTablePagination from "./DataTablePagination";
import DataTableToolbar from "./DataTableToolbar";
import DateRangeCustomInput from "./DateRangeCustomInput";
import SelectionSummary from "./SelectionSummary";
import SingleDateCustomInput from "./SingleDateCustomInput";

import {
  Paper,
  Table,
  TableContainer,
  TableHead,
} from "@mui/material";
import 'react-datepicker/dist/react-datepicker.css';
import "./CustomDataTable.scss";



// Wrapper components moved out of render to avoid redefining on each render
function DateRangeCustomInputWrapper(props) {
  // dateRanges and setOpenDatePicker will be injected via closure from CustomDataTable
  // We'll assign these as variables below
  return (
    <DateRangeCustomInput
      {...props}
      dateRanges={DateRangeCustomInputWrapper.dateRanges}
      setOpenDatePicker={DateRangeCustomInputWrapper.setOpenDatePicker}
    />
  );
}

function SingleDateCustomInputWrapper(props) {
  return (
    <SingleDateCustomInput
      {...props}
      singleDates={SingleDateCustomInputWrapper.singleDates}
      setOpenDatePicker={SingleDateCustomInputWrapper.setOpenDatePicker}
    />
  );
}

const CustomDataTable = forwardRef(({
  columns = [],
  records = [],
  isLoading = false,
  pageSize = 10,
  totalRecords = 0,
  setPageIndex = () => { },
  handleSorting = () => { },
  setSearchArry = () => { },
  onResetAll = () => { },
  onRowSelect = () => { },
  onDownload = () => { },
  showDownload = false,
  isSelectAll = false,
  setIsSelectAll = () => { },
  onNameClick = () => { },
  pageIndex = 0,
  showCreateNew = false,
  onCreateNew = null,
  toolbarActions = [],
  initialSortConfig,
  initialRowSelection = [],
  showAddStores = false,
  onAddStores = () => { },
  showMoveStores = false,
  onMoveStores = () => { },
  firstPageIndex = null,
  showSelectionSummary = false,
  showActionMenu = false,
  actionItems = [],
  onActionClick = () => {},
  disabledHeaders = [],
  hideToolbar = false,
  wrapperClassName = '',
  hideSelectAll = false,
  hideCheckbox = false,
  tableMinWidth = null,
  tableMinHeight = null,
  hidePagination = false,
  hideFilterRow = false,
  emptyStateConfig = {},
  hideResetButton = false,
  hideSelectionSummary = false,
  initialSelectedCount = 0,
  disableSelectionPersistence = false,
}, ref) => {
  const {
    dateRanges,
    singleDates,
    openDatePicker,
    selectedRows,
    globallySelectedRows,
    globalSelectAll,
    allFilteredSelected,
    selectAllTotal,
    sortConfig,
    filter,
    totalPages,
    partialSelectAll,
    deselectedItems,
    setOpenDatePicker,
    handleResetAll,
    handleSort,
    handleSelectAll,
    handleRowSelect,
    handleFilterChange,
    handleDateRangeChange,
    handleSingleDateChange,
  } = useCustomDataTable({
    columns,
    initialSortConfig,
    records,
    pageSize,
    onResetAll,
    onRowSelect,
    handleSorting,
    setSearchArry,
    setPageIndex,
    isSelectAll,
    totalRecords,
    pageIndex,
    initialRowSelection,
    firstPageIndex,
    setIsSelectAll,
    disableSelectionPersistence,
  });

  useImperativeHandle(ref, () => ({
    handleResetAll,
  }));

  // Inject latest props into wrapper components via static assignment
  DateRangeCustomInputWrapper.dateRanges = dateRanges;
  DateRangeCustomInputWrapper.setOpenDatePicker = setOpenDatePicker;
  SingleDateCustomInputWrapper.singleDates = singleDates;
  SingleDateCustomInputWrapper.setOpenDatePicker = setOpenDatePicker;

  let selectedCount;
  if (partialSelectAll) {
    selectedCount = selectAllTotal - deselectedItems.size;
  } else if (isSelectAll || globalSelectAll || allFilteredSelected) {
    selectedCount = totalRecords;
  } else {
    selectedCount = disableSelectionPersistence 
      ? (globallySelectedRows?.size || selectedRows?.length || 0)
      : (initialSelectedCount || globallySelectedRows?.size || selectedRows?.length || 0);
  }

    const getToolbarActions = () => {
    const baseActions = [...toolbarActions];
    if (showCreateNew) {
      baseActions.push({
        id: 'create-new',
        label: 'Create New',
        onClick: onCreateNew,
        icon: <Icon icon="add" size="small" />,
        variant: "secondary",
        isDark: true
      });
    }
    if (showDownload) {
      baseActions.push({
        id: 'download',
        label: 'Download',
        onClick: () => {
          // When select all is active (with or without filters), pass empty array
          // so API returns all filtered records. Otherwise pass selected row IDs.
          const isAllSelected = globalSelectAll || allFilteredSelected || partialSelectAll;
          onDownload(isAllSelected ? [] : selectedRows);
        },
        icon: <Icon icon="download" size="small" />,
        variant: "outlined",
        isDark: true,
        requiresSelection: true
      });
    }
    if (showAddStores) {
      baseActions.push({
        id: 'add-stores',
        label: 'Add Stores',
        onClick: onAddStores,
        icon: <Icon icon="add_box" size="large" />,
        variant: "secondary",
        isDark: true,
        requiresSelection: false,
        disabled: false,
      });
    }
    if (showMoveStores) {
      baseActions.push({
        id: 'move-stores',
        label: 'Move Stores',
        onClick: onMoveStores,
        variant: "secondary",
        isDark: true,
        disabled: true,
        requiresSelection: true,
      });
    }
    return baseActions;
  }

  // Extract page calculation
  let calculatedPage;
  if (firstPageIndex == 1) {
    calculatedPage = pageIndex;
  } else if (pageIndex >= 0) {
    calculatedPage = pageIndex + 1;
  } else {
    calculatedPage = 1;
  }

  return (
    <div data-testid="custom-datatable" className={wrapperClassName}>
      {!hideToolbar && (
        <DataTableToolbar
          onResetAll={handleResetAll}
          selectedRowsCount={selectedCount}
          showActionMenu={showActionMenu}
          actionItems={actionItems}
          onActionClick={onActionClick}
          toolbarActions={getToolbarActions()}
          hideResetButton={hideResetButton}
        />
      )}
      {!hideSelectionSummary && (
        <SelectionSummary selectedCount={selectedCount} showSelectionSummary={showSelectionSummary}/>
      )}
      <TableContainer
        component={Paper}
        className={`table-scroll-container${records.length > 5 ? ' table-scroll-container--minheight' : ''}`}
      >
        <Table
          className="dark-mode-table"
          style={
            tableMinWidth || tableMinHeight
              ? {
                  ...(tableMinWidth ? { minWidth: tableMinWidth } : {}),
                  ...(tableMinHeight ? { minHeight: tableMinHeight } : {}),
                }
              : undefined
          }
        >
          <TableHead>
            <DataTableHead
              columns={columns}
              sortConfig={sortConfig}
              handleSort={handleSort}
              isSelectAll={isSelectAll}
              records={records}
              selectedRows={selectedRows}
              handleSelectAll={handleSelectAll}
              partialSelectAll={partialSelectAll}
              deselectedItems={deselectedItems}
              disabledHeaders={disabledHeaders}
              hideSelectAll={hideSelectAll}
              hideCheckbox={hideCheckbox}
              initialSelectedCount={initialSelectedCount}
              totalRecords={totalRecords}
              disableSelectionPersistence={disableSelectionPersistence}
            />
            {!hideFilterRow && (
              <DataTableFilterRow
                columns={columns}
                dateRanges={dateRanges}
                singleDates={singleDates}
                openDatePicker={openDatePicker}
                filter={filter}
                setOpenDatePicker={setOpenDatePicker}
                handleDateRangeChange={handleDateRangeChange}
                handleSingleDateChange={handleSingleDateChange}
                handleFilterChange={handleFilterChange}
                DateRangeCustomInput={DateRangeCustomInputWrapper}
                SingleDateCustomInput={SingleDateCustomInputWrapper}
                hideSelectAll={hideSelectAll}
              />
            )}
          </TableHead>
          <DataTableBody
            columns={columns}
            records={records}
            isLoading={isLoading}
            selectedRows={selectedRows}
            handleRowSelect={handleRowSelect}
            handleResetAll={handleResetAll}
            onNameClick={onNameClick}
            EmptyState={EmptyState}
            ShimmerTableRow={ShimmerTableRow}
            partialSelectAll={partialSelectAll}
            deselectedItems={deselectedItems}
            hideSelectAll={hideSelectAll}
            hideCheckbox={hideCheckbox}
            emptyStateConfig={emptyStateConfig}
          />
        </Table>
      </TableContainer>
      {!hidePagination && (
        <div className="sticky-pagination">
          <DataTablePagination
            totalPages={Number.isInteger(totalPages) && totalPages > 0 ? totalPages : 1}
            page={calculatedPage}
            handlePageChange={(value) => {
              if(firstPageIndex == 1) {
                setPageIndex(value);
              } else {
                setPageIndex(value - 1);
              }
            }}
          />
        </div>
      )}
    </div>
  );
});

export default CustomDataTable;
